import { TeamName } from "@/components/TeamName";
import { AppointmentStatusIndicator } from "@/features/appointments/AppointmentStatusIndicator";
import { getAppointmentTitle } from "@/features/calendar/utils";
import { useModals } from "@/features/modals/hooks";
import { CardTableLoader } from "@/features/workspace/widgets/Components";
import styled from "@emotion/styled";
import { type Appointment, type WidgetSize } from "@repo/api";
import { Table as SkapaTable, TableBody, Text, Variables } from "@repo/skapa";
import {
  CoWorkerName,
  LocalizedDateTimeRange,
  translateAppointmentType,
  translateLocationType,
  useSettings,
} from "@repo/ui";
import { getFullName } from "@repo/utils";

const CompactTable = styled(SkapaTable)`
  && tbody td {
    padding-top: ${Variables.space50};
    padding-bottom: ${Variables.space50};
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;
`;

const SecondRow = styled.div`
  color: ${Variables.colourTextAndIcon2};
`;

const AppointmentRow = ({
  appointment,
  widgetSize,
}: {
  appointment: Appointment;
  widgetSize: WidgetSize;
}) => {
  const { pushModal } = useModals();
  const { general } = useSettings();
  const {
    appointmentType,
    type,
    customer,
    assignedCoworker,
    assignedTeam,
    orderNumber,
    appointmentStatus,
    timeWindowStartTime,
    timeWindowEndTime,
    locationType,
  } = appointment;

  const firstName = customer?.firstName ?? "";
  const lastName = customer?.lastName ?? "";

  return (
    <ClickableRow
      onClick={() => {
        pushModal({
          type: "CALENDAR_APPOINTMENT",
          appointment: appointment,
        });
      }}
    >
      <td>
        <Text textStyle="Heading.XS">
          {translateAppointmentType(appointmentType)}
        </Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            {type === "BOKA_APPOINTMENT" ? "BOKA" : "Drop-in"}
          </Text>
        </SecondRow>
      </td>

      <td>
        <Text textStyle="Heading.XS">
          <LocalizedDateTimeRange
            fromDate={timeWindowStartTime}
            toDate={timeWindowEndTime}
          />
        </Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            {translateLocationType(locationType)}
          </Text>
        </SecondRow>
      </td>

      <td>
        <Text textStyle="Heading.XS">{getAppointmentTitle(appointment)}</Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            {getFullName(
              firstName,
              lastName,
              general?.localization?.reverse_names
            )}
          </Text>
        </SecondRow>
      </td>

      <td>
        <Text textStyle="Heading.XS">
          <AppointmentStatusIndicator status={appointmentStatus} />
        </Text>
      </td>

      {widgetSize === "L" ? (
        <td>
          {assignedCoworker ? (
            <CoWorkerName email={assignedCoworker} textStyle="Heading.XS" />
          ) : (
            <TeamName teamId={assignedTeam} textStyle="Heading.XS" />
          )}
          <SecondRow>
            <Text textStyle="Body.S">{orderNumber}</Text>
          </SecondRow>
        </td>
      ) : null}
    </ClickableRow>
  );
};

export const AppointmentsTable = ({
  appointments,
  size,
  isLoading,
  isValidating,
}: {
  appointments: Appointment[];
  size: WidgetSize;
  isLoading: boolean;
  isValidating: boolean;
}) => {
  if (isLoading || isValidating) {
    return <CardTableLoader />;
  }

  return (
    <CompactTable fullWidth aria-label="Appointments">
      <TableBody striped>
        {appointments.map((appointment) => (
          <AppointmentRow
            key={appointment.id}
            appointment={appointment}
            widgetSize={size}
          />
        ))}
      </TableBody>
    </CompactTable>
  );
};
